import {FC, useState} from 'react'
import styles from './index.module.css'
import Swal from 'sweetalert2'
import { Input } from '../Input/Input'
import { Button } from '../Button/Button'
import { POST } from '../../services/api'
import { getStructureText } from '../../services/structureCorrection'
import useModals from '../../hooks/useModals'
import { Loading } from '../Loading/Loading'

type ContentModalDescriptionIAType = {
  description: string
  productName: string
  productImage: string | null
  pluginIA: string
  onDescriptionGenerated: (description: string) => void
  onCancel?: () => void
}

type StyleOption = {
  value: string
  label: string
  systemPrompt: string
  typeCorrectionStructure: string
}

const ContentModalDescriptionIA: FC<ContentModalDescriptionIAType> = ({ 
  description, 
  productName, 
  productImage, 
  pluginIA,
  onDescriptionGenerated,
  onCancel 
}) => {
  const [descriptionIA, setDescriptionIA] = useState<string>(description)
  const [extraDetails, setExtraDetails] = useState<string>('')
  const [selectedStyle, setSelectedStyle] = useState<string>('marketing')
  const [loading, setLoading] = useState(false)
  const [loadingImageAnalysis, setLoadingImageAnalysis] = useState(false)
  const { closeWrapperModal } = useModals()

  const styleOptions: StyleOption[] = [
    {
      value: 'marketing',
      label: 'Marketing',
      systemPrompt: 'Atue como um profissional de marketing e coloque o texto enviado em um contexto de vendas, apresentando o produto de forma atrativa. Entregue apenas o texto trabalhado',
      typeCorrectionStructure: 'marketing-structure'
    },
    {
      value: 'correction',
      label: 'Correção Textual',
      systemPrompt: 'Atue como um revisor de texto e sugira uma correção para o texto enviado, apresentando apenas o texto corrigido',
      typeCorrectionStructure: 'revision-structure'
    },
    {
      value: 'objective',
      label: 'Objetividade',
      systemPrompt: 'Atue como um revisor de texto e torne o texto mais objetivo e direto, mantendo as informações essenciais. Apresente apenas o texto trabalhado',
      typeCorrectionStructure: 'objective-structure'
    },
    {
      value: 'young',
      label: 'Jovem',
      systemPrompt: 'Atue como um profissional de marketing e adapte o texto para um público jovem, usando linguagem moderna e dinâmica. Apresente apenas o texto trabalhado',
      typeCorrectionStructure: 'young-structure'
    },
    {
      value: 'classic',
      label: 'Clássico',
      systemPrompt: 'Atue como um profissional de marketing e adapte o texto para um estilo clássico e elegante, usando linguagem formal e sofisticada. Apresente apenas o texto trabalhado',
      typeCorrectionStructure: 'classic-structure'
    },
    {
      value: 'contemporary',
      label: 'Contemporâneo',
      systemPrompt: 'Atue como um profissional de marketing e adapte o texto para um estilo contemporâneo e atual, usando linguagem moderna e acessível. Apresente apenas o texto trabalhado',
      typeCorrectionStructure: 'contemporary-structure'
    },
    {
      value: 'fun',
      label: 'Divertido',
      systemPrompt: 'Atue como um profissional de marketing e adapte o texto para um tom divertido e descontraído, usando linguagem criativa e envolvente. Apresente apenas o texto trabalhado',
      typeCorrectionStructure: 'fun-structure'
    }
  ]

  const generateDescriptionIAModal = async () => {
    if (!descriptionIA.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Atenção',
        text: 'Por favor, insira uma descrição para ser processada pela IA.',
      })
      return
    }

    setLoading(true)
    try {
      const selectedStyleOption = styleOptions.find(option => option.value === selectedStyle)
      
      if (!selectedStyleOption) {
        throw new Error('Estilo selecionado não encontrado')
      }

      const basePrompt = descriptionIA === '' || descriptionIA === productName
        ? `Crie a descrição do produto com base no nome que estou passando. Segue o nome: ${productName}`
        : `Corrija ou melhore a descrição do produto que estou passando. Segue a descrição: ${descriptionIA}`
      
      const finalPrompt = extraDetails 
        ? `${basePrompt}\n\nInformações adicionais sobre o produto: ${extraDetails}`
        : basePrompt

      const result = await POST('structure-correction-ia/converse-structure', {
        prompt: finalPrompt,
        modelId: pluginIA,
        systemPrompt: `${selectedStyleOption.systemPrompt}. Retorne somente texto simples, sem Markdown, com no máximo 512 caracteres, incluindo espaços e quebras de linha. Não invente características do produto.`,
        typeCorrectionStructure: selectedStyleOption.typeCorrectionStructure,
      })

      let generatedDescription = getStructureText(result).trim()
      if (generatedDescription.length > 512) {
        const shortenedResult = await POST('structure-correction-ia/converse-structure', {
          prompt: `Informações originais do produto:\n${finalPrompt}\n\nDescrição a resumir:\n${generatedDescription}`,
          modelId: pluginIA,
          systemPrompt: 'Resuma a descrição a seguir mantendo o estilo e apenas características presentes nas informações originais do produto. Retorne somente a descrição em texto simples, sem Markdown. Busque no máximo 400 caracteres e nunca ultrapasse 512 caracteres, contando espaços e quebras de linha. Entregue frases completas; não invente características.',
        })
        generatedDescription = getStructureText(shortenedResult).trim()
      }
      if (generatedDescription.length > 512) {
        throw new Error('A descrição gerada excedeu o limite de 512 caracteres mesmo após a revisão. Tente novamente.')
      }
      setDescriptionIA(generatedDescription)
    } catch (e) {
      console.log('Erro ao chamar API IA (Descrição):', e)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: e instanceof Error ? e.message : String(e),
      })
    } finally {
      setLoading(false)
    }
  }

  const generateDescriptionByImage = async () => {
    if (!productImage) {
      Swal.fire({
        icon: 'warning',
        title: 'Atenção',
        text: 'Nenhuma imagem disponível para análise.',
      })
      return
    }

    try {
      const bucket = 'img.ninegrid.com'
      const pathfileArray = productImage.split("img.ninegrid.com/")[1].split("/")
      const sourcePathS3 = `${pathfileArray[0]}/${pathfileArray[1]}`
      const nameFile = pathfileArray[2]
      
      setLoadingImageAnalysis(true)
      
      const res = await POST(`/structure-correction-ia/analyzeImage`, {
        bucket,
        nameFile,
        sourcePathS3,
      })

      // Adiciona um delay antes de verificar a resposta
      await new Promise((resolve) => setTimeout(resolve, 3000))

      if (!res?.data || typeof res.data !== 'string') {
        throw new Error('Resposta inválida da análise de imagem')
      }

      const MAX_LENGTH = 512
      const truncatedDescription = res.data.length > MAX_LENGTH
        ? res.data.substring(0, MAX_LENGTH)
        : res.data

      setDescriptionIA(truncatedDescription)
    } catch (e) {
      console.log('Erro ao analisar imagem:', e)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao analisar imagem. Tente novamente.',
      })
    } finally {
      setLoadingImageAnalysis(false)
    }
  }

  const handleSave = () => {
    if (!descriptionIA.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Atenção',
        text: 'A descrição não pode estar vazia.',
      })
      return
    }

    onDescriptionGenerated(descriptionIA)
    closeWrapperModal()
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      closeWrapperModal()
    }
  }

  return (
    <div className={styles.modalContent}>
      {/* Botão de fechar */}
      <button
        className={styles.closeButton}
        onClick={handleCancel}
        type="button"
        disabled={loading || loadingImageAnalysis}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      <div className={styles.header}>
        <h3 className={styles.title}>Geração de Descrição com IA</h3>
        <p className={styles.subtitle}>
          Aprimore a descrição do seu produto usando inteligência artificial
        </p>
      </div>

      <div className={styles.content}>
        {/* Coluna Esquerda */}
        <div className={styles.leftColumn}>
          {/* Campo de entrada da descrição */}
          <div className={styles.inputSection}>
            <Input
              inputAttr={{
                type: 'textarea',
                'data-field': 'description',
              }}
              placeholder="Digite ou cole a descrição do produto aqui..."
              label="Descrição do Produto"
              value={descriptionIA}
              change={(value: string) => {
                setDescriptionIA(value)
              }}
              maxCaracteres={512}
              rows={4}
            />
            <div className={styles.characterCounter}>
              {descriptionIA.length} de 512 caracteres
            </div>
          </div>

          {/* Campo de detalhes extras */}
          <div className={styles.inputSection}>
            <Input
              inputAttr={{
                type: 'textarea',
              }}
              placeholder="Informações adicionais sobre o produto (opcional)..."
              label="Detalhes Extras"
              value={extraDetails}
              change={(value: string) => {
                setExtraDetails(value)
              }}
              maxCaracteres={200}
              rows={2}
            />
            <div className={styles.characterCounter}>
              {extraDetails.length} de 200 caracteres
            </div>
          </div>

          {/* Seleção de estilo */}
          <div className={styles.styleSection}>
            <Input
              inputAttr={{
                type: 'select',
              }}
              label="Estilo da Descrição"
              options={styleOptions.map(option => ({
                value: option.value,
                select: option.label
              }))}
              value={selectedStyle}
              change={(value: string) => {
                setSelectedStyle(value)
              }}
            />
          </div>
        </div>

        {/* Coluna Direita */}
        <div className={styles.rightColumn}>
          {/* Imagem do produto (se disponível) */}
          {productImage && (
            <div className={styles.imageSection}>
              <label className={styles.imageLabel}>Imagem do Produto</label>
              <div className={styles.imageContainer}>
                <img 
                  src={productImage} 
                  alt="Produto" 
                  className={styles.productImage}
                />
                <Button
                  text="Gerar pela Imagem"
                  color='info'
                  size='small'
                  horizontalPadding={4}
                  svgIcon={
                    <svg width='12' height='12' viewBox='0 0 15 15' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path d='M6.39844 1.92578L6.78125 0.886719C6.80859 0.804688 6.89062 0.75 7 0.75C7.08203 0.75 7.16406 0.804688 7.19141 0.886719L7.57422 1.92578L8.61328 2.30859C8.69531 2.33594 8.75 2.41797 8.75 2.5C8.75 2.60938 8.69531 2.69141 8.61328 2.71875L7.57422 3.10156L7.19141 4.11328C7.16406 4.19531 7.08203 4.25 7 4.25C6.89062 4.25 6.80859 4.19531 6.78125 4.11328L6.39844 3.10156L5.38672 2.71875C5.30469 2.69141 5.25 2.60938 5.25 2.5C5.25 2.41797 5.30469 2.33594 5.38672 2.30859L6.39844 1.92578Z' fill='white'/>
                    </svg>
                  }
                  btnAttr={{
                    type: 'button',
                  }}
                  click={generateDescriptionByImage}
                  btnClass='fw-bolder d-flex flex-row gap-1 align-items-center'
                  txtClass='text-nowrap text-white'
                  outline={false}
                  disabled={loading || loadingImageAnalysis}
                />
              </div>
            </div>
          )}

          {/* Botão principal de geração */}
          <div className={styles.generateSection}>
            <Button
              text="Gerar Descrição com IA"
              color='success'
              size='medium'
              horizontalPadding={6}
              svgIcon={
                <svg width='15' height='15' viewBox='0 0 15 15' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path d='M6.39844 1.92578L6.78125 0.886719C6.80859 0.804688 6.89062 0.75 7 0.75C7.08203 0.75 7.16406 0.804688 7.19141 0.886719L7.57422 1.92578L8.61328 2.30859C8.69531 2.33594 8.75 2.41797 8.75 2.5C8.75 2.60938 8.69531 2.69141 8.61328 2.71875L7.57422 3.10156L7.19141 4.11328C7.16406 4.19531 7.08203 4.25 7 4.25C6.89062 4.25 6.80859 4.19531 6.78125 4.11328L6.39844 3.10156L5.38672 2.71875C5.30469 2.69141 5.25 2.60938 5.25 2.5C5.25 2.41797 5.30469 2.33594 5.38672 2.30859L6.39844 1.92578Z' fill='white'/>
                </svg>
              }
              btnAttr={{
                type: 'button',
              }}
              click={generateDescriptionIAModal}
              btnClass='fw-bolder d-flex flex-row gap-2 align-items-center justify-content-center w-100'
              txtClass='text-nowrap text-white'
              outline={false}
              disabled={loading || loadingImageAnalysis || !descriptionIA.trim()}
            />
          </div>

          {/* Dica sobre Detalhes Extras */}
          <div className={styles.hintSection}>
            <div className={styles.hintIcon}>💡</div>
            <div className={styles.hintContent}>
              <strong>Sobre "Detalhes Extras":</strong>
              <p>Use este campo para informações que a IA deve considerar:</p>
              <ul>
                <li><strong>Características técnicas:</strong> dimensões, peso, material</li>
                <li><strong>Público-alvo:</strong> "para crianças", "uso profissional"</li>
                <li><strong>Diferenciais:</strong> "único no mercado", "tecnologia exclusiva"</li>
                <li><strong>Contexto de uso:</strong> "ideal para casa", "resistente ao tempo"</li>
                <li><strong>Benefícios específicos</strong> que você quer destacar</li>
              </ul>
              <small>A IA combina descrição base + detalhes extras + estilo + imagem para criar descrições mais ricas!</small>
            </div>
          </div>

          {/* Loading states */}
          {(loading || loadingImageAnalysis) && (
            <div className={styles.loadingContainer}>
              <Loading />
              <span className={styles.loadingText}>
                {loadingImageAnalysis 
                  ? 'Analisando imagem...' 
                  : 'Gerando descrição...'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Botões de ação */}
      <div className={styles.actionButtons}>
        <Button
          text="Cancelar"
          color='secondary'
          size='medium'
          horizontalPadding={6}
          btnAttr={{
            type: 'button',
          }}
          click={handleCancel}
          btnClass='fw-bolder'
          txtClass='text-nowrap'
          outline={true}
          disabled={loading || loadingImageAnalysis}
        />
        <Button
          text="Usar Descrição"
          color='primary'
          size='medium'
          horizontalPadding={6}
          btnAttr={{
            type: 'button',
          }}
          click={handleSave}
          btnClass='fw-bolder'
          txtClass='text-nowrap'
          disabled={loading || loadingImageAnalysis || !descriptionIA.trim()}
        />
      </div>
    </div>
  )
}

export {ContentModalDescriptionIA}